import { coreApi } from '$api'
import {
  IRemunerationTimelineRecord,
  ISalaryWeekContainer,
  RemunerationTimelineRecord,
} from '$models'
import { singletonQueryClient } from '$utils'
import { Dayjs } from 'dayjs'

enum QueryKeys {
  getSalaryWeekContainers = 'salary-week-containers',
}

/**
 * Add a new RemunerationTimelineRecord
 * @param remunerationTimelineRecord the remuneration timeline record to be created
 * @returns Promise<IRemunerationTimelineRecord>
 */
export const add = async (
  remunerationTimelineRecord: IRemunerationTimelineRecord
): Promise<IRemunerationTimelineRecord> => {
  return await coreApi.createRemunerationTimelineRecord(
    remunerationTimelineRecord as RemunerationTimelineRecord
  )
}

/**
 * Update an existing RemunerationTimelineRecord
 * @param remunerationTimelineRecord the remuneration timeline record to be updated
 * @returns Promise<IRemunerationTimelineRecord>
 */
export const update = async (
  remunerationTimelineRecord: IRemunerationTimelineRecord
): Promise<IRemunerationTimelineRecord | null> => {
  if (!remunerationTimelineRecord.id) {
    throw new Error('RemunerationTimelineRecord id is required')
  }

  return await coreApi.updateRemunerationTimelineRecord(
    remunerationTimelineRecord.id,
    remunerationTimelineRecord as RemunerationTimelineRecord
  )
}

/**
 * Delete a existing remuneration timeline
 * @param id the remuneration timeline id to be deleted
 * @returns Promise
 */
export const remove = async (id: string): Promise<void> => {
  return await coreApi.deleteRemunerationTimelineRecord(id)
}

/**
 * Get SalaryWeekContainers for the given date range and staff member IDs
 * @param reportStartDate The start date of the report
 * @param reportEndDate The end date of the report
 * @param staffMemberIds List of staff member IDs to filter by
 * @param includeCrossVenueShifts When true, cross-venue shifts are included in ReportShifts. Defaults to false
 * @param processClockins When true, active clockins are processed into salary cost calculations. Defaults to true
 * @param useLockedHourlyRatesIfRosterIsLocked When true, uses locked hourly rates from a locked roster. Defaults to false
 */
export const getSalaryWeekContainers = async (
  reportStartDate: Dayjs,
  reportEndDate: Dayjs,
  staffMemberIds?: string[],
  includeCrossVenueShifts = false,
  processClockins = true,
  useLockedHourlyRatesIfRosterIsLocked = false
): Promise<{
  [key: string]: ISalaryWeekContainer[]
}> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getSalaryWeekContainers,
      reportStartDate,
      reportEndDate,
      staffMemberIds,
      includeCrossVenueShifts,
      processClockins,
      useLockedHourlyRatesIfRosterIsLocked,
    ],
    queryFn: async () => {
      const salaryWeekContainers = await coreApi.getSalaryWeekContainers(
        reportStartDate,
        reportEndDate,
        staffMemberIds,
        includeCrossVenueShifts,
        processClockins,
        useLockedHourlyRatesIfRosterIsLocked
      )

      return salaryWeekContainers
    },
  })
}
